import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { LobbyClient } from 'boardgame.io/client';
import { VERSION } from '../version.js';
import { useTranslation } from '../i18n/index.js';
import './RematchModal.css';

const GAME_NAME = 'waterloo';

/**
 * RematchModal - Shows when game ends to coordinate rematch between players.
 *
 * Uses boardgame.io chat system for player-to-player communication.
 * Supports partial rematch (continuing without players who decline).
 */
export default function RematchModal({
  serverUrl,
  playerID,
  numPlayers,
  chatMessages,
  sendChatMessage,
  onJoinMatch,
  gameOver,
  playerNames,
}) {
  const { t } = useTranslation();
  // Helper to get player display name
  const getPlayerName = (id) => playerNames?.[id] || `Player ${id}`;

  const [view, setView] = useState('rematch');  // 'rematch' | 'create' | 'join' | 'name'
  const [playerName, setPlayerName] = useState(() =>
    localStorage.getItem('waterloo-name') || `Player ${playerID}`
  );
  const [newNumPlayers, setNewNumPlayers] = useState(2);
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [joining, setJoining] = useState(false);

  const lobbyClient = useMemo(() => new LobbyClient({ server: serverUrl }), [serverUrl]);

  // Derive rematch state from chat messages
  const rematchState = useMemo(() => {
    const state = {
      requestedBy: null,
      acceptedBy: new Set(),
      declinedBy: new Set(),
      newMatchID: null,
      creatorID: null,
    };

    for (const msg of chatMessages || []) {
      const payload = msg.payload;
      if (!payload?.type) continue;

      if (payload.type === 'rematch_request') {
        if (!state.requestedBy) {
          state.requestedBy = payload.from;
          state.acceptedBy.add(payload.from);
        }
      } else if (payload.type === 'rematch_accept') {
        state.acceptedBy.add(payload.from);
      } else if (payload.type === 'rematch_decline') {
        state.declinedBy.add(payload.from);
        state.acceptedBy.delete(payload.from);
      } else if (payload.type === 'rematch_cancel') {
        state.requestedBy = null;
        state.acceptedBy.clear();
        state.declinedBy.clear();
      } else if (payload.type === 'rematch_ready') {
        state.newMatchID = payload.newMatchID;
        state.creatorID = payload.from;
      }
    }

    return state;
  }, [chatMessages]);

  // Build player status list
  const playerStatuses = useMemo(() => {
    const statuses = [];
    for (let i = 0; i < numPlayers; i++) {
      const id = String(i);
      let status = 'pending';
      if (rematchState.acceptedBy.has(id)) {
        status = 'accepted';
      } else if (rematchState.declinedBy.has(id)) {
        status = 'declined';
      }
      statuses.push({
        id,
        status,
        isYou: id === playerID,
        isRequester: id === rematchState.requestedBy,
      });
    }
    return statuses;
  }, [numPlayers, playerID, rematchState]);

  // Check if rematch can start (2+ players accepted)
  const canStartRematch = rematchState.acceptedBy.size >= 2;

  // Check if this player has already responded
  const hasResponded = rematchState.acceptedBy.has(playerID) || rematchState.declinedBy.has(playerID);

  // Check if only 1 player left (not enough for rematch)
  const notEnoughPlayers = rematchState.requestedBy &&
    (numPlayers - rematchState.declinedBy.size) < 2;

  // Request rematch
  const requestRematch = useCallback(() => {
    sendChatMessage({ type: 'rematch_request', from: playerID });
  }, [sendChatMessage, playerID]);

  // Accept rematch
  const acceptRematch = useCallback(() => {
    sendChatMessage({ type: 'rematch_accept', from: playerID });
  }, [sendChatMessage, playerID]);

  // Decline rematch
  const declineRematch = useCallback(() => {
    sendChatMessage({ type: 'rematch_decline', from: playerID });
  }, [sendChatMessage, playerID]);

  // Cancel rematch (requester only)
  const cancelRematch = useCallback(() => {
    sendChatMessage({ type: 'rematch_cancel', from: playerID });
  }, [sendChatMessage, playerID]);

  // Start the rematch - create new match and notify others
  const startRematch = useCallback(async () => {
    if (!canStartRematch || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const acceptedCount = rematchState.acceptedBy.size;

      // Create new match
      const { matchID: newMatchID } = await lobbyClient.createMatch(GAME_NAME, {
        numPlayers: acceptedCount,
      });

      // Join as player 0
      const { playerCredentials } = await lobbyClient.joinMatch(
        GAME_NAME,
        newMatchID,
        { playerName }
      );

      // Broadcast new match ID to others
      sendChatMessage({ type: 'rematch_ready', newMatchID, from: playerID });

      // Join the match
      onJoinMatch({
        matchID: newMatchID,
        playerID: '0',
        credentials: playerCredentials,
        numPlayers: acceptedCount,
      });
    } catch (err) {
      setError(t('error.createFailed', { message: err.message }));
      setIsLoading(false);
    }
  }, [canStartRematch, isLoading, rematchState.acceptedBy.size, lobbyClient, playerName, sendChatMessage, playerID, onJoinMatch, t]);

  // Auto-join when new match is ready (for non-creators)
  useEffect(() => {
    if (!rematchState.newMatchID || joining) return;
    if (rematchState.creatorID === playerID) return; // Creator already joined
    if (!rematchState.acceptedBy.has(playerID)) return; // Only joiners who accepted

    setJoining(true);

    const joinNewMatch = async () => {
      try {
        // Wait a bit for creator to join first
        await new Promise(resolve => setTimeout(resolve, 500));

        // Get match to find available slot
        const match = await lobbyClient.getMatch(GAME_NAME, rematchState.newMatchID);
        const availableSlot = match.players.find(p => !p.name);

        if (!availableSlot) {
          setError(t('error.matchFull'));
          setJoining(false);
          return;
        }

        const { playerID: newPlayerID, playerCredentials } = await lobbyClient.joinMatch(
          GAME_NAME,
          rematchState.newMatchID,
          { playerName }
        );

        onJoinMatch({
          matchID: rematchState.newMatchID,
          playerID: newPlayerID,
          credentials: playerCredentials,
          numPlayers: match.players.length,
        });
      } catch (err) {
        setError(t('error.rematchFailed', { message: err.message }));
        setJoining(false);
      }
    };

    joinNewMatch();
  }, [rematchState.newMatchID, rematchState.creatorID, rematchState.acceptedBy, playerID, joining, lobbyClient, playerName, onJoinMatch, t]);

  // Save name
  const saveName = () => {
    if (playerName.trim()) {
      localStorage.setItem('waterloo-name', playerName.trim());
      setView('rematch');
    }
  };

  // Create new match (different from rematch)
  const createMatch = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { matchID: newMatchID } = await lobbyClient.createMatch(GAME_NAME, {
        numPlayers: newNumPlayers,
      });

      const { playerID: newPlayerID, playerCredentials } = await lobbyClient.joinMatch(
        GAME_NAME,
        newMatchID,
        { playerName: playerName.trim() }
      );

      onJoinMatch({
        matchID: newMatchID,
        playerID: newPlayerID,
        credentials: playerCredentials,
        numPlayers: newNumPlayers,
      });
    } catch (err) {
      setError(t('error.createFailed', { message: err.message }));
    } finally {
      setIsLoading(false);
    }
  };

  // Join existing match
  const joinMatch = async () => {
    if (!joinCode.trim()) {
      setError(t('error.enterMatchCode'));
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const match = await lobbyClient.getMatch(GAME_NAME, joinCode.trim());
      const availableSlot = match.players.find(p => !p.name);

      if (!availableSlot) {
        setError(t('error.matchFull'));
        return;
      }

      const { playerID: newPlayerID, playerCredentials } = await lobbyClient.joinMatch(
        GAME_NAME,
        joinCode.trim(),
        { playerName: playerName.trim() }
      );

      onJoinMatch({
        matchID: joinCode.trim(),
        playerID: newPlayerID,
        credentials: playerCredentials,
        numPlayers: match.players.length,
      });
    } catch (err) {
      setError(t('error.joinFailed', { message: err.message }));
    } finally {
      setIsLoading(false);
    }
  };

  // Show joining state
  if (joining || rematchState.newMatchID) {
    return (
      <div className="rematch-modal-overlay">
        <div className="rematch-modal">
          <h2>{t('rematch.joiningRematch')}</h2>
          <p className="joining-text">{t('rematch.connecting')}</p>
          {error && <p className="error">{error}</p>}
          <div className="modal-version">v{VERSION}</div>
        </div>
      </div>
    );
  }

  // Name entry view
  if (view === 'name') {
    return (
      <div className="rematch-modal-overlay">
        <div className="rematch-modal">
          <h2>{t('button.changeName')}</h2>
          <input
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder={t('lobby.yourName')}
            maxLength={20}
            onKeyDown={(e) => e.key === 'Enter' && saveName()}
            autoFocus
          />
          <div className="button-row">
            <button className="secondary" onClick={() => setView('rematch')}>{t('button.back')}</button>
            <button onClick={saveName} disabled={!playerName.trim()}>{t('button.save')}</button>
          </div>
          <div className="modal-version">v{VERSION}</div>
        </div>
      </div>
    );
  }

  // Create match view
  if (view === 'create') {
    return (
      <div className="rematch-modal-overlay">
        <div className="rematch-modal">
          <h2>{t('lobby.createMatch')}</h2>
          <label>
            {t('lobby.numPlayers')}
            <select value={newNumPlayers} onChange={(e) => setNewNumPlayers(Number(e.target.value))}>
              <option value={2}>{t('lobby.players2')}</option>
              <option value={3}>{t('lobby.players3')}</option>
              <option value={4}>{t('lobby.players4')}</option>
            </select>
          </label>
          {error && <p className="error">{error}</p>}
          <div className="button-row">
            <button className="secondary" onClick={() => setView('rematch')}>{t('button.back')}</button>
            <button onClick={createMatch} disabled={isLoading}>
              {isLoading ? t('button.creating') : t('button.create')}
            </button>
          </div>
          <div className="modal-version">v{VERSION}</div>
        </div>
      </div>
    );
  }

  // Join match view
  if (view === 'join') {
    return (
      <div className="rematch-modal-overlay">
        <div className="rematch-modal">
          <h2>{t('lobby.joinMatch')}</h2>
          <input
            type="text"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            placeholder={t('lobby.matchCode')}
            onKeyDown={(e) => e.key === 'Enter' && joinMatch()}
            autoFocus
          />
          {error && <p className="error">{error}</p>}
          <div className="button-row">
            <button className="secondary" onClick={() => setView('rematch')}>{t('button.back')}</button>
            <button onClick={joinMatch} disabled={isLoading || !joinCode.trim()}>
              {isLoading ? t('button.joining') : t('button.join')}
            </button>
          </div>
          <div className="modal-version">v{VERSION}</div>
        </div>
      </div>
    );
  }

  // Main rematch view
  return (
    <div className="rematch-modal-overlay">
      <div className="rematch-modal">
        <h2>{t('rematch.gameOver')}</h2>

        {/* Winner announcement */}
        <div className="game-result">
          {gameOver?.winner !== undefined
            ? t('game.wins', { playerName: getPlayerName(gameOver.winner) })
            : t('game.draw')}
        </div>

        {/* Rematch section */}
        <div className="rematch-section">
          {!rematchState.requestedBy ? (
            // No rematch requested yet
            <button className="primary rematch-btn" onClick={requestRematch}>
              {t('rematch.askForRematch')}
            </button>
          ) : notEnoughPlayers ? (
            // Not enough players for rematch
            <div className="rematch-status-message">
              <p>{t('rematch.notEnoughPlayers')}</p>
              <button className="secondary" onClick={cancelRematch}>{t('button.ok')}</button>
            </div>
          ) : hasResponded ? (
            // This player has responded - show status
            <div className="rematch-waiting">
              <h3>{t('rematch.status')}</h3>
              <div className="player-list">
                {playerStatuses.map((p) => (
                  <div key={p.id} className={`player-status ${p.status}`}>
                    <span className="status-indicator"></span>
                    <span className="player-label">
                      {getPlayerName(p.id)}
                      {p.isYou && ` ${t('lobby.you')}`}
                      {p.isRequester && ` - ${t('rematch.requested')}`}
                    </span>
                    <span className="status-text">
                      {p.status === 'accepted' && t('rematch.ready')}
                      {p.status === 'pending' && t('rematch.pending')}
                      {p.status === 'declined' && t('rematch.declined')}
                    </span>
                  </div>
                ))}
              </div>

              {canStartRematch && rematchState.requestedBy === playerID && (
                <button className="primary" onClick={startRematch} disabled={isLoading}>
                  {isLoading ? t('rematch.starting') : t('rematch.startMatch', { count: rematchState.acceptedBy.size })}
                </button>
              )}

              {rematchState.requestedBy === playerID && (
                <button className="secondary cancel-btn" onClick={cancelRematch}>
                  {t('rematch.cancelRematch')}
                </button>
              )}
            </div>
          ) : (
            // Rematch requested by someone else - show accept/decline
            <div className="rematch-invite">
              <p>{t('rematch.wantsRematch', { playerName: getPlayerName(rematchState.requestedBy) })}</p>
              <div className="player-list compact">
                {playerStatuses.filter(p => p.status !== 'pending' || p.isYou).map((p) => (
                  <div key={p.id} className={`player-status ${p.status}`}>
                    <span className="status-indicator"></span>
                    <span className="player-label">
                      {getPlayerName(p.id)}
                      {p.isYou && ` ${t('lobby.you')}`}
                    </span>
                  </div>
                ))}
              </div>
              <button className="primary" onClick={acceptRematch}>
                {t('rematch.accept')}
              </button>
              <button className="secondary" onClick={declineRematch}>
                {t('rematch.decline')}
              </button>
            </div>
          )}
        </div>

        {error && <p className="error">{error}</p>}

        {/* Other options */}
        <div className="other-options">
          <span className="divider-text">{t('rematch.orStartDifferent')}</span>
          <div className="option-buttons">
            <button className="option-btn" onClick={() => setView('create')}>
              {t('lobby.createMatch')}
            </button>
            <button className="option-btn" onClick={() => setView('join')}>
              {t('lobby.joinMatch')}
            </button>
            <button className="option-btn" onClick={() => setView('name')}>
              {t('button.changeName')}
            </button>
          </div>
        </div>
        <div className="modal-version">v{VERSION}</div>
      </div>
    </div>
  );
}
